import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import AGMasterDetailTable from 'components/shared/AGTable/AGMasterDetailTable';
import Icon from 'components/shared/Icon/Icon';

import AssignedUsersCell from './components/AssignedUsersCell';
import CheckboxWithChevron from './components/CheckboxWithChevron';
import OnboardingDetailRenderer from './components/OnboardingDetailRenderer';
import SelectionActionsBar from './components/SelectionActionsBar';
import TableFilters from './components/TableFilters';
import TablePagination from './components/TablePagination';
import { DEFAULT_COLUMNS, MIN_ITEMS_FOR_CONTROLS, mergeAssignments } from './lib/helpers';

const DelegateTable = ({
  steps,
  setSteps,
  delegationSteps,
  onDelegateRow,
  page,
  setPage,
  limit,
  setLimit,
  totalItems,
  filters,
  setFilters,
  actions,
  hideActionsBar = false
}) => {
  const [columnDefs, setColumnDefs] = useState(DEFAULT_COLUMNS);
  const [shouldShowControls, setShouldShowControls] = useState(false);

  const mergedSteps = useMemo(() => {
    const merged = mergeAssignments(delegationSteps || [], steps || []);
    return merged.filter((step) => step.substeps && step.substeps.length > 0);
  }, [delegationSteps, steps]);

  const hasFiltersApplied = Object.keys(filters || {}).length > 0;
  const showControls = shouldShowControls || totalItems >= MIN_ITEMS_FOR_CONTROLS || page > 1;

  useEffect(() => {
    if (!hasFiltersApplied && totalItems >= MIN_ITEMS_FOR_CONTROLS) {
      setShouldShowControls(true);
    }
  }, [totalItems, hasFiltersApplied]);

  const displaySteps = mergedSteps;

  const changeFilters = useCallback(
    (updater) => {
      setFilters((prevFilters) => {
        const currentFilters = prevFilters || {};
        const newFilters = typeof updater === 'function' ? updater(currentFilters) : updater;
        return newFilters || {};
      });
      setPage(1);
    },
    [setFilters, setPage]
  );

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleColumnChange = (updatedColumns) => {
    setColumnDefs(updatedColumns);
  };

  const handleColumnDragEnd = (updatedColumns) => {
    setColumnDefs(updatedColumns);
  };

  const onStepToggle = (stepCode) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => {
        if (step.code === stepCode) {
          const hasCheckedSubsteps = step.substeps.some((sub) => sub.checked);
          const newCheckedState = !hasCheckedSubsteps;
          return {
            ...step,
            checked: newCheckedState,
            substeps: step.substeps.map((sub) => ({ ...sub, checked: newCheckedState }))
          };
        }
        return step;
      })
    );
  };

  const onSubstepToggle = (substepCode) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        substeps: step.substeps.map((sub) =>
          sub.code === substepCode ? { ...sub, checked: !sub.checked } : sub
        )
      }))
    );
  };

  const allChecked = useMemo(() => {
    return steps?.length > 0 && steps.every((step) => step.checked);
  }, [steps]);

  const someChecked = useMemo(() => {
    return steps?.some((step) => step.checked || step.substeps?.some((sub) => sub.checked));
  }, [steps]);

  const selectedRows = useMemo(() => {
    const rows = [];
    steps?.forEach((step) => {
      step.substeps?.filter((sub) => sub.checked).forEach((sub) => rows.push(sub));
    });
    return rows;
  }, [steps]);

  const clearSelection = useCallback(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        checked: false,
        substeps: step.substeps.map((sub) => ({ ...sub, checked: false }))
      }))
    );
  }, [setSteps]);

  const handleSelectAll = useCallback(() => {
    const newCheckedState = !someChecked;
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        checked: newCheckedState,
        substeps: step.substeps.map((sub) => ({ ...sub, checked: newCheckedState }))
      }))
    );
  }, [someChecked, setSteps]);

  const columns = useMemo(() => {
    return columnDefs
      .filter((col) => !col.hide)
      .map((col) => {
        if (col.field === 'selector') {
          return {
            ...col,
            headerComponent: () => (
              <div className="flex items-center pl-2">
                <div
                  className="flex h-4 w-4 cursor-pointer items-center justify-center"
                  onClick={handleSelectAll}>
                  {someChecked ? (
                    <Icon icon="minus-filled" size={20} color="primary" />
                  ) : (
                    <div className="h-[16px] w-[16px] rounded border border-solid border-[#cdd5d8] bg-white" />
                  )}
                </div>
              </div>
            ),
            cellRenderer: ({ data, node }) => (
              <CheckboxWithChevron data={data} node={node} onStepToggle={onStepToggle} />
            )
          };
        }
        if (col.field === 'assignments') {
          return {
            ...col,
            cellRenderer: ({ data }) => <AssignedUsersCell data={data} />
          };
        }
        return col;
      });
  }, [columnDefs, onStepToggle, allChecked, someChecked, handleSelectAll]);

  return (
    <div
      className={cn(
        'relative flex h-full flex-col overflow-hidden rounded-lg border border-solid border-[#E9EAEB] bg-white'
      )}>
      {showControls && (
        <TableFilters
          filters={filters}
          changeFilters={changeFilters}
          columnDefs={columnDefs}
          onColumnChange={handleColumnChange}
          onColumnDragEnd={handleColumnDragEnd}
        />
      )}

      <div
        className={cn(
          'min-h-0 flex-1',
          '[--ag-background-color:#FFFFFF] [--ag-header-background-color:#FFFFFF] [--ag-row-border-color:#E9EAEB]',
          '[--ag-borders:none]',
          '[--ag-range-selection-border-color:transparent]',
          '[&_.ag-cell-focus]:!border-transparent [&_.ag-cell-focus]:!outline-transparent',
          '[&_.ag-row]:bg-white',
          '[&_.ag-row.ag-row-group-expanded]:!bg-[#F0F9FF]',
          '[&_.ag-details-row]:!bg-transparent [&_.ag-details-row]:!p-0',
          showControls ? '[&_.ag-root-wrapper]:!rounded-none' : '[&_.ag-root-wrapper]:!rounded-lg'
        )}>
        <AGMasterDetailTable
          tableHeight="100%"
          data={displaySteps}
          detailComponent={OnboardingDetailRenderer}
          columns={columns}
          onToggleMaster={onStepToggle}
          onToggleDetail={onSubstepToggle}
          onDelegateRow={onDelegateRow}
        />
      </div>

      {showControls && (
        <TablePagination
          page={page}
          limit={limit}
          totalItems={totalItems}
          setLimit={setLimit}
          setPage={setPage}
          onPageChange={handlePageChange}
        />
      )}

      <SelectionActionsBar
        selectedCount={selectedRows.length}
        onClearSelection={clearSelection}
        actions={actions}
        isHidden={hideActionsBar}
      />
    </div>
  );
};

export default DelegateTable;
